import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Aml.css';
import AmlNav from './AmlNav';
import useAmlFramework from './useAmlFramework';
import { canManageAml } from './amlPermissions';
import {
  listAmlFilings, createAmlFiling, submitAmlFiling, uploadAmlFilingReceipt,
  updateAmlFilingStatus, reconcileAmlFiling, updateAmlFiling, deleteAmlFiling,
  downloadAmlFilingReceipt, saveBlob, importDecidedCases,
} from './amlApi';

const statusClass = (s) => (s === 'SUBMITTED' ? 'aml-pill-submitted' : s === 'IN_PROGRESS' ? 'aml-pill-inprogress' : 'aml-pill-muted');
const statusLabel = (s) => (s === 'SUBMITTED' ? 'Submitted' : s === 'IN_PROGRESS' ? 'In progress' : 'Not started');
// Recurring values the backend rolls over; a legacy free-text frequency shows as-is and never recurs.
const FREQUENCIES = [
  { value: '', label: 'One-time / event-driven' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'SEMIANNUAL', label: 'Semiannual' },
  { value: 'ANNUAL', label: 'Annual' },
];
const frequencyLabel = (v) => FREQUENCIES.find((o) => o.value === (v || ''))?.label || v;

export default function AmlFilings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canManage = canManageAml();
  const { status: fwStatus, frameworks, frameworkId, setFrameworkId } = useAmlFramework();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('month'); // 'month' | 'all'
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', regulator: 'UAFE', frequency: '', dueDate: '', owner: '', confidential: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitErrorId, setSubmitErrorId] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [uploadingId, setUploadingId] = useState(null);
  const [importingCases, setImportingCases] = useState(false);
  const [caseImportResult, setCaseImportResult] = useState(null);
  const [reconcileForm, setReconcileForm] = useState({ filedCount: '', monitoringEngineCount: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = () => {
    if (!frameworkId) return;
    setLoading(true);
    listAmlFilings(frameworkId).then((rows) => { setFilings(rows); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [frameworkId]);

  // Due dates are compared as local YYYY-MM-DD text, the form the API sends, so a filing due
  // today counts as due and not as overdue.
  const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const todayIso = isoDay(today);
  const in30Iso = isoDay(new Date(today.getTime() + 30 * 86400000));
  const isOverdue = (f) => !!f.dueDate && f.dueDate < todayIso && f.status !== 'SUBMITTED';
  // A submitted filing is done — it leaves "due" the same way it leaves "overdue".
  const dueSoon = filings.filter((f) => f.status !== 'SUBMITTED' && (!f.dueDate || (f.dueDate >= todayIso && f.dueDate <= in30Iso)));
  // The default view is what needs attention now: overdue filings first, then the next 30 days.
  // Without the overdue ones the tile said "Overdue 1" while the list showed nothing.
  const monthFiltered = [...filings.filter(isOverdue), ...dueSoon];
  const visible = view === 'month' ? monthFiltered : filings;

  const dueThisMonth = dueSoon.length;
  const inProgress = filings.filter((f) => f.status === 'IN_PROGRESS').length;
  const submittedThisYear = filings.filter((f) => f.status === 'SUBMITTED' && f.submittedAt
    && new Date(f.submittedAt).getFullYear() === today.getFullYear()).length;
  const overdue = filings.filter(isOverdue).length;

  const submitAdd = async () => {
    if (!form.name.trim()) { setError(t('Aml.nameRequired', 'A name is required.')); return; }
    setSaving(true);
    setError('');
    try {
      await createAmlFiling(frameworkId, form);
      setShowAdd(false);
      setForm({ name: '', regulator: 'UAFE', frequency: '', dueDate: '', owner: '', confidential: false });
      load();
    } catch (e) {
      setError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the filing.'));
    } finally {
      setSaving(false);
    }
  };

  // The receipt is a direct file upload (not the generic Evidence module — see the backend
  // migration comment on why) and is required before submit() will succeed.
  const fetchReceipt = async (f) => {
    try {
      saveBlob(await downloadAmlFilingReceipt(frameworkId, f.id), f.receiptFileName);
    } catch (e) {
      setSubmitError(t('Aml.downloadFailed', 'Could not download the file.'));
    }
  };

  const uploadReceipt = async (id, file) => {
    if (!file) return;
    setUploadingId(id);
    setSubmitErrorId(id);
    setSubmitError('');
    try {
      await uploadAmlFilingReceipt(frameworkId, id, file);
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.uploadFailed', 'Could not upload the receipt.'));
    } finally {
      setUploadingId(null);
    }
  };

  const markSubmitted = async (id) => {
    setSubmitErrorId(id);
    setSubmitError('');
    try {
      await submitAmlFiling(frameworkId, id);
      setSubmitErrorId(null);
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.submitFailed', 'A receipt must be uploaded first.'));
    }
  };

  const setStatus = async (id, status) => {
    setSubmitErrorId(id);
    setSubmitError('');
    try {
      await updateAmlFilingStatus(frameworkId, id, status);
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the filing.'));
    }
  };

  const doReconcile = async (id) => {
    const filed = reconcileForm.filedCount === '' ? null : Number(reconcileForm.filedCount);
    const engine = reconcileForm.monitoringEngineCount === '' ? null : Number(reconcileForm.monitoringEngineCount);
    setSubmitErrorId(id);
    setSubmitError('');
    if (!Number.isInteger(filed) || !Number.isInteger(engine) || filed < 0 || engine < 0) {
      setSubmitError(t('Aml.reconcileCounts', 'Enter both counts as whole numbers of 0 or more.'));
      return;
    }
    try {
      await reconcileAmlFiling(frameworkId, id, filed, engine);
      setReconcileForm({ filedCount: '', monitoringEngineCount: '' });
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.reconcileFailed', 'Could not record the reconciliation.'));
    }
  };

  // Correcting a filing that was entered wrongly. Errors show under the row's action bar, in the
  // same place as the submit errors.
  const openEdit = (f) => {
    setEditId(f.id);
    setEditForm({ name: f.name, regulator: f.regulator, frequency: f.frequency || '', dueDate: f.dueDate || '',
      owner: f.owner || '', confidential: !!f.confidential });
    setSubmitErrorId(null);
  };

  const saveEdit = async (id) => {
    setSubmitErrorId(id);
    setSubmitError('');
    if (!editForm.name.trim()) { setSubmitError(t('Aml.nameRequired', 'A name is required.')); return; }
    setEditSaving(true);
    try {
      await updateAmlFiling(frameworkId, id, editForm);
      setEditId(null);
      setSubmitErrorId(null);
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.saveFailed', 'Could not save the filing.'));
    } finally {
      setEditSaving(false);
    }
  };

  const removeFiling = async (f) => {
    if (!window.confirm(t('Aml.deleteFilingConfirm', 'Delete filing "{{name}}"? This cannot be undone.', { name: f.name }))) return;
    setSubmitErrorId(f.id);
    setSubmitError('');
    try {
      await deleteAmlFiling(frameworkId, f.id);
      setExpandedId(null);
      setEditId(null);
      setSubmitErrorId(null);
      load();
    } catch (e) {
      setSubmitError(e?.response?.data?.message || t('Aml.deleteFailed', 'Could not delete.'));
    }
  };

  if (fwStatus === 'loading' || loading) return <div className="aml-page"><div className="aml-spinner" /></div>;

  if (fwStatus === 'error') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.loadError', 'Could not load the AML Program. Your session may have expired — try reloading.')}
        </div>
      </div>
    );
  }

  if (fwStatus === 'not-enrolled') {
    return (
      <div className="aml-page">
        <div className="aml-empty">
          {t('Aml.notEnrolled', 'Your organization has not enrolled in an AML framework yet. Enroll in SEPS-IGT-2025-0113 or another AML framework from the Frameworks page to activate the AML Program.')}
          <div style={{ marginTop: '0.75rem' }}>
            <button className="aml-btn aml-btn-primary" onClick={() => navigate('/dashboard/compliance/frameworks')}>
              {t('Aml.goToFrameworks', 'Go to Frameworks')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="aml-page">
      <div className="aml-header">
        <div>
          <h1 className="aml-title">{t('Aml.filingsTitle', 'Filings')}</h1>
          <p className="aml-subtitle">
            {t('Aml.filingsSubtitle', 'UAFE and SEPS reporting obligations. You file in the regulator’s portal; CATAAM keeps the schedule, the reconciliation and the receipt.')}
          </p>
        </div>
      </div>

      <div className="aml-stat-bar">
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statDueThisMonth', 'DUE THIS MONTH')}</div><div className="aml-stat-val">{dueThisMonth}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statInProgress', 'IN PROGRESS')}</div><div className="aml-stat-val">{inProgress}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statSubmittedYear', 'SUBMITTED THIS YEAR')}</div><div className="aml-stat-val aml-val-ok">{submittedThisYear}</div></div>
        <div className="aml-stat"><div className="aml-stat-label">{t('Aml.statOverdueCol', 'OVERDUE')}</div><div className={`aml-stat-val ${overdue > 0 ? 'aml-val-bad' : ''}`}>{overdue}</div></div>
      </div>

      <AmlNav frameworks={frameworks} frameworkId={frameworkId} onFrameworkChange={setFrameworkId} />

      <div className="aml-toolbar">
        <div className="aml-toolbar-group">
          <button className={`aml-btn ${view === 'month' ? 'aml-btn-primary' : ''}`} onClick={() => setView('month')}>{t('Aml.thisMonth', 'This month')}</button>
          <button className={`aml-btn ${view === 'all' ? 'aml-btn-primary' : ''}`} onClick={() => setView('all')}>{t('Aml.allFilings', 'All filings')}</button>
        </div>
        {canManage && (
          <div className="aml-toolbar-group">
            <label className="aml-btn" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              {importingCases ? t('Aml.importing', 'Importing…') : t('Aml.importDecidedCases', 'Import decided cases (CSV)')}
              <input type="file" accept=".csv" style={{ display: 'none' }} disabled={importingCases}
                onChange={async (e) => {
                  const file = e.target.files[0];
                  e.target.value = '';
                  if (!file) return;
                  setImportingCases(true);
                  setCaseImportResult(null);
                  try {
                    setCaseImportResult({ ok: true, ...(await importDecidedCases(frameworkId, file)) });
                    load();
                  } catch (err) {
                    setCaseImportResult({ ok: false, message: err?.response?.data?.message || t('Aml.importFailed', 'Import failed.') });
                  } finally {
                    setImportingCases(false);
                  }
                }} />
            </label>
            <button className="aml-btn aml-btn-primary" onClick={() => setShowAdd((s) => !s)}>{t('Aml.addFiling', '+ Add filing')}</button>
          </div>
        )}
      </div>

      {caseImportResult && (
        <div className="aml-section" style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem' }}>
          {caseImportResult.ok ? (
            <span>
              {t('Aml.caseImportSummary', '{{created}} case filings added, {{existing}} already on the calendar.',
                { created: caseImportResult.created, existing: caseImportResult.existing })}
              {caseImportResult.skipped?.length > 0 && (
                <span className="aml-muted"> · {t('Aml.importSkipped', '{{count}} rows skipped', { count: caseImportResult.skipped.length })}</span>
              )}
            </span>
          ) : (
            <span style={{ color: '#b91c1c' }}>{caseImportResult.message}</span>
          )}
        </div>
      )}

      {showAdd && (
        <div className="aml-section" style={{ padding: '1rem' }}>
          {error && <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            <label>
              <div className="aml-expand-label">{t('Aml.filingName', 'Name')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.regulator', 'Regulator')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.regulator} onChange={(e) => setForm({ ...form, regulator: e.target.value })}>
                <option value="UAFE">UAFE</option><option value="SEPS">SEPS</option><option value="INTERNAL">{t('Aml.regulatorInternal', 'Internal')}</option>
              </select>
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.frequency', 'Frequency')}</div>
              <select className="aml-select" style={{ width: '100%' }} value={form.frequency}
                onChange={(e) => setForm({ ...form, frequency: e.target.value })}>
                {FREQUENCIES.map((o) => (
                  <option key={o.value} value={o.value}>{t(`Aml.frequency${o.value || 'OneTime'}`, o.label)}</option>
                ))}
              </select>
              {form.frequency && (
                <div className="aml-muted" style={{ fontSize: '0.6875rem', marginTop: '0.25rem' }}>
                  {t('Aml.recurrenceHint', 'The next occurrence is created automatically when this one is submitted or falls due.')}
                </div>
              )}
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.dueDate', 'Due date')}</div>
              <input type="date" className="aml-input" style={{ width: '100%' }} value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
            </label>
            <label>
              <div className="aml-expand-label">{t('Aml.ownerCol', 'OWNER')}</div>
              <input className="aml-input" style={{ width: '100%' }} value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-end', gap: '0.375rem' }}>
              <input type="checkbox" checked={form.confidential} onChange={(e) => setForm({ ...form, confidential: e.target.checked })} />
              <span style={{ fontSize: '0.8125rem' }}>{t('Aml.restrictedToComplianceOfficer', 'Restricted to compliance officer (e.g. ROII)')}</span>
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
            <button className="aml-btn aml-btn-primary" disabled={saving} onClick={submitAdd}>{t('Aml.save', 'Save')}</button>
            <button className="aml-btn" onClick={() => setShowAdd(false)}>{t('Aml.cancel', 'Cancel')}</button>
          </div>
        </div>
      )}

      <div className="aml-section">
        <div className="aml-table-wrap">
          <table className="aml-table">
            <thead>
              <tr>
                <th>{t('Aml.filing', 'FILING')}</th><th>{t('Aml.regulatorCol', 'REGULATOR')}</th><th>{t('Aml.frequencyCol', 'FREQUENCY')}</th>
                <th>{t('Aml.due', 'DUE')}</th><th>{t('Aml.statusCol', 'STATUS')}</th><th />
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={6} className="aml-empty">{t('Aml.noFilings', 'No filings in this view.')}</td></tr>
              )}
              {visible.map((f) => (
                <React.Fragment key={f.id}>
                  <tr>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {f.name}
                        {f.autoGenerated && (
                          <span className="aml-badge aml-pill-muted" style={{ marginLeft: '0.375rem' }}
                            title={t('Aml.autoGeneratedNote', 'Created automatically from the previous occurrence')}>
                            {t('Aml.autoGenerated', 'Auto')}
                          </span>
                        )}
                      </div>
                      {f.confidential && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('Aml.confidentialNote', 'Visible to the compliance officer only')}</div>}
                      {isOverdue(f) && <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#dc2626' }}>{t('Aml.overdueNote', 'Overdue')}</div>}
                    </td>
                    <td><span className="aml-badge aml-pill-muted">{f.regulator}</span></td>
                    <td style={{ color: '#475569' }}>
                      {!f.frequency ? '—'
                        : FREQUENCIES.some((o) => o.value === f.frequency)
                          ? t(`Aml.frequency${f.frequency}`, frequencyLabel(f.frequency))
                          : f.frequency}
                    </td>
                    <td style={{ color: '#475569' }}>{f.dueDate || '—'}</td>
                    <td>
                      <span className={`aml-pill ${f.confidential ? 'aml-pill-restricted' : statusClass(f.status)}`}>
                        {f.confidential ? t('Aml.restricted', 'Restricted') : t(`Aml.status${f.status}`, statusLabel(f.status))}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="aml-btn" style={{ height: 30, padding: '0 0.625rem', fontSize: '0.8125rem' }}
                        onClick={() => {
                          setExpandedId(expandedId === f.id ? null : f.id);
                          setSubmitErrorId(null);
                        }}>
                        {expandedId === f.id ? t('Aml.hide', 'Hide ▴') : t('Aml.view', 'View ▾')}
                      </button>
                    </td>
                  </tr>
                  {expandedId === f.id && (
                    <tr className="aml-expand-row">
                      <td colSpan={6}>
                        <div className="aml-expand-grid">
                          <div><div className="aml-expand-label">{t('Aml.ownerCol', 'OWNER')}</div>{f.owner || '—'}</div>
                          <div><div className="aml-expand-label">{t('Aml.linkedControl', 'LINKED CONTROL')}</div>{f.controlId ? `#${f.controlId}` : '—'}</div>
                          {f.caseReference && (
                            <div><div className="aml-expand-label">{t('Aml.caseRefCol', 'CASE REF')}</div><span className="aml-badge-mono">{f.caseReference}</span></div>
                          )}
                          <div>
                            <div className="aml-expand-label">{t('Aml.receipt', 'RECEIPT')}</div>
                            {f.receiptFileName ? (
                              <button type="button" className="aml-link-btn" onClick={() => fetchReceipt(f)}>📎 {f.receiptFileName}</button>
                            ) : t('Aml.noReceipt', 'None yet')}
                          </div>
                        </div>

                        {f.status !== 'SUBMITTED' && canManage && (
                          <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.625rem', flexWrap: 'wrap' }}>
                            <label className="aml-btn" style={{ cursor: 'pointer' }}>
                              {uploadingId === f.id ? t('Aml.uploading', 'Uploading…') : t('Aml.uploadReceipt', 'Upload receipt')}
                              <input type="file" style={{ display: 'none' }} disabled={uploadingId === f.id}
                                onChange={(e) => uploadReceipt(f.id, e.target.files[0])} />
                            </label>
                            <button className="aml-btn aml-btn-accent" disabled={!f.receiptFileName}
                              title={!f.receiptFileName ? t('Aml.receiptFirst', 'Upload a receipt first') : undefined}
                              onClick={() => markSubmitted(f.id)}>
                              {t('Aml.markSubmitted', 'Mark as submitted')}
                            </button>
                            <select className="aml-select" value={f.status}
                              onChange={(e) => setStatus(f.id, e.target.value)}>
                              <option value="NOT_STARTED">{t('Aml.statusNOT_STARTED', 'Not started')}</option>
                              <option value="IN_PROGRESS">{t('Aml.statusIN_PROGRESS', 'In progress')}</option>
                            </select>
                            <button className="aml-btn" onClick={() => (editId === f.id ? setEditId(null) : openEdit(f))}>
                              {t('Aml.editFiling', 'Edit')}
                            </button>
                            <button className="aml-btn" style={{ color: '#b91c1c' }} onClick={() => removeFiling(f)}>
                              {t('Aml.deleteFiling', 'Delete')}
                            </button>
                          </div>
                        )}
                        {f.status !== 'SUBMITTED' && editId === f.id && editForm && (
                          <div style={{ marginTop: '0.75rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
                              <label>
                                <div className="aml-expand-label">{t('Aml.filingName', 'Name')}</div>
                                <input className="aml-input" style={{ width: '100%' }} value={editForm.name}
                                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.regulator', 'Regulator')}</div>
                                <select className="aml-select" style={{ width: '100%' }} value={editForm.regulator}
                                  onChange={(e) => setEditForm({ ...editForm, regulator: e.target.value })}>
                                  <option value="UAFE">UAFE</option><option value="SEPS">SEPS</option><option value="INTERNAL">{t('Aml.regulatorInternal', 'Internal')}</option>
                                </select>
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.frequency', 'Frequency')}</div>
                                <select className="aml-select" style={{ width: '100%' }} value={editForm.frequency}
                                  onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}>
                                  {!FREQUENCIES.some((o) => o.value === editForm.frequency) && (
                                    <option value={editForm.frequency}>{editForm.frequency}</option>
                                  )}
                                  {FREQUENCIES.map((o) => (
                                    <option key={o.value} value={o.value}>{t(`Aml.frequency${o.value || 'OneTime'}`, o.label)}</option>
                                  ))}
                                </select>
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.dueDate', 'Due date')}</div>
                                <input type="date" className="aml-input" style={{ width: '100%' }} value={editForm.dueDate}
                                  onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })} />
                              </label>
                              <label>
                                <div className="aml-expand-label">{t('Aml.ownerCol', 'OWNER')}</div>
                                <input className="aml-input" style={{ width: '100%' }} value={editForm.owner}
                                  onChange={(e) => setEditForm({ ...editForm, owner: e.target.value })} />
                              </label>
                              <label style={{ display: 'flex', alignItems: 'flex-end', gap: '0.375rem' }}>
                                <input type="checkbox" checked={editForm.confidential}
                                  onChange={(e) => setEditForm({ ...editForm, confidential: e.target.checked })} />
                                <span style={{ fontSize: '0.8125rem' }}>{t('Aml.restrictedToComplianceOfficer', 'Restricted to compliance officer (e.g. ROII)')}</span>
                              </label>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.625rem' }}>
                              <button className="aml-btn aml-btn-primary" disabled={editSaving} onClick={() => saveEdit(f.id)}>{t('Aml.save', 'Save')}</button>
                              <button className="aml-btn" onClick={() => setEditId(null)}>{t('Aml.cancel', 'Cancel')}</button>
                            </div>
                          </div>
                        )}

                        <div style={{ marginTop: '1rem' }}>
                          <div className="aml-expand-label">{t('Aml.reconciliation', 'RECONCILIATION')}</div>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '0.375rem' }}>
                            {canManage && (
                              <>
                                <label>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('Aml.filedCount', 'Filed count')}</div>
                                  <input type="number" className="aml-input" style={{ width: 110 }}
                                    value={reconcileForm.filedCount}
                                    onChange={(e) => setReconcileForm({ ...reconcileForm, filedCount: e.target.value })} />
                                </label>
                                <label>
                                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{t('Aml.monitoringCount', 'Monitoring engine count')}</div>
                                  <input type="number" className="aml-input" style={{ width: 160 }}
                                    value={reconcileForm.monitoringEngineCount}
                                    onChange={(e) => setReconcileForm({ ...reconcileForm, monitoringEngineCount: e.target.value })} />
                                </label>
                                <button className="aml-btn" onClick={() => doReconcile(f.id)}>{t('Aml.reconcile', 'Reconcile')}</button>
                              </>
                            )}
                            {!canManage && !f.reconciliationStatus && (
                              <span className="aml-muted" style={{ fontSize: '0.8125rem' }}>{t('Aml.notReconciled', 'Not reconciled yet.')}</span>
                            )}
                            {f.reconciliationStatus && (
                              <span className={`aml-pill ${f.reconciliationStatus === 'MATCHED' ? 'aml-pill-pass' : 'aml-pill-fail'}`}>
                                {f.reconciliationStatus === 'MATCHED' ? t('Aml.matched', 'Matched') : t('Aml.mismatched', 'Mismatched')}
                                {' '}({f.filedCount ?? '—'} / {f.monitoringEngineCount ?? '—'})
                              </span>
                            )}
                          </div>
                        </div>
                        {submitErrorId === f.id && submitError && (
                          <div style={{ color: '#b91c1c', fontSize: '0.8125rem', marginTop: '0.75rem' }}>{submitError}</div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="aml-table-foot">
          <span>{t('Aml.showing', '{{count}} of {{total}}', { count: visible.length, total: filings.length })}</span>
        </div>
      </div>
    </div>
  );
}
